/**
 * PENDING CALCULATIONS (Tom 2026-10-13, "(A)"): the moment a save goes out,
 * the calculations it will make stale show "⏳" — without waiting for the
 * server's own stale flag to come back.
 *
 * Which calculations: the server's rule (`_aggregate_trigger_roots` +
 * `collect_affected_expressions_batched`, models/rflp/_base.py), DIRECT
 * case only, followed through chains:
 *   - a changed field on a record is a trigger on that record; a changed
 *     `name` matches every mention of it (a rename);
 *   - a changed AttributeValue `value` is a trigger on its owner, named
 *     after its attribute;
 *   - a calculation is hit when its Mentions edge to the record lists the
 *     trigger in `paths` (or a path under it: `children` covers
 *     `children.mass_kg`);
 *   - a hit calculation's result (owner, attribute) is a trigger for the
 *     next hop;
 *   - a changed physical part is also a change of every logical part whose
 *     SELECTED allocation is that part, with the same triggers (the server's
 *     read-through step: calculations mention `lp.mass_kg` and read the
 *     allocated part's value through it) — at the start only, never on a
 *     chain hop.
 * Not copied: the climb up containment parents (roll-ups over children) —
 * calculations reached only that way show "⏳" when the server's flag
 * arrives, as before.
 *
 * A mark goes when the server takes over (the calculation arrives stale),
 * when a fresh result arrives (its value changes, or it was evaluated
 * again) — regardless of anything else — when the save fails, or at the
 * latest SETTLE_MS after the save was confirmed, so a mark the server never
 * confirms cannot stay.
 */

import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { useAtomValue } from "jotai";
import { graphStore, graphStoreJotaiStore } from "@sourcepilot/framework/graph-store";
import { onSave, type SaveEvent } from "@sourcepilot/framework/editable-wrapper";

/** the latest a mark outlives its confirmed save */
export const SETTLE_MS = 30_000;
/** the server's BFS depth cap */
const MAX_DEPTH = 20;

/** trigger names per record; `null` = every mention (a rename) */
export type Roots = Map<string, Set<string> | null>;

const targetIds = (id: string, prop: string): string[] =>
  graphStore.getRelationships(id, prop).map((r) => r.targetId);

/** What a save changes, as the server's triggers. */
export function rootsOfSave(event: SaveEvent): Roots {
  const roots: Roots = new Map();
  const merge = (id: string, triggers: Set<string> | null) => {
    if (roots.has(id) && roots.get(id) === null) return;
    if (triggers === null) { roots.set(id, null); return; }
    const had = roots.get(id) ?? new Set<string>();
    triggers.forEach((t) => had.add(t));
    roots.set(id, had);
  };
  if (event.fields.length > 0) {
    merge(event.nodeId, event.fields.includes("name") ? null : new Set(event.fields));
  }
  if (event.modelName === "AttributeValue" && event.fields.includes("value")) {
    const owner = targetIds(event.nodeId, "owner")[0];
    const attribute = graphStore.getTargetNodes<any>(event.nodeId, "attribute")[0];
    const name = attribute?.name as string | undefined;
    if (owner && name) merge(owner, new Set([name]));
  }
  return roots;
}

const hits = (triggers: Set<string> | null, paths: string[]): boolean =>
  triggers === null ||
  Array.from(triggers).some((t) => paths.includes(t) || paths.some((p) => p.startsWith(`${t}.`)));

/** The calculations the server marks stale for these triggers (direct
 *  mentions, through chains), read from the live data. */
export function affectedExpressions(roots: Roots): Set<string> {
  const affected = new Set<string>();
  const seen = new Set<string>();
  const key = (id: string, t: Set<string> | null) =>
    `${id}|${t === null ? "*" : Array.from(t).sort().join(",")}`;
  let frontier: Array<[string, Set<string> | null]> = [];
  roots.forEach((t, id) => {
    if (!seen.has(key(id, t))) { seen.add(key(id, t)); frontier.push([id, t]); }
  });
  // the read-through step: a physical part's change reaches the logical
  // parts it is the selected allocation of (the reverse of `allocated_to`)
  for (const [entity, triggers] of Array.from(frontier)) {
    for (const rel of graphStore.getRelationships(entity, "allocation_sources")) {
      if (rel.properties?.is_selected !== true) continue;
      if (!seen.has(key(rel.targetId, triggers))) {
        seen.add(key(rel.targetId, triggers));
        frontier.push([rel.targetId, triggers]);
      }
    }
  }
  for (let depth = 0; frontier.length > 0 && depth < MAX_DEPTH; depth++) {
    const next = new Map<string, Set<string>>();
    for (const [entity, triggers] of frontier) {
      // the reverse index of Expression.mentions, with the edge's `paths`
      for (const rel of graphStore.getRelationships(entity, "mentioned_in_expressions")) {
        const exprId = rel.targetId;
        if (affected.has(exprId)) continue;
        const paths = Array.isArray(rel.properties?.paths) ? (rel.properties.paths as string[]) : [];
        if (!hits(triggers, paths)) continue;
        affected.add(exprId);
        for (const dvId of targetIds(exprId, "result")) {
          const owner = targetIds(dvId, "owner")[0];
          const name = graphStore.getTargetNodes<any>(dvId, "attribute")[0]?.name as string | undefined;
          if (owner && name) {
            const t = next.get(owner) ?? new Set<string>();
            t.add(name);
            next.set(owner, t);
          }
        }
      }
    }
    frontier = [];
    next.forEach((t, id) => {
      if (!seen.has(key(id, t))) { seen.add(key(id, t)); frontier.push([id, t]); }
    });
  }
  return affected;
}

// ---------------------------------------------------------------------------
// the pending marks
// ---------------------------------------------------------------------------

/** calculation id → the save that marked it */
const pendingAtom = atom<ReadonlyMap<string, number>>(new Map());
const isPendingAtom = atomFamily((id: string) => atom((get) => get(pendingAtom).has(id)));

/** per marked calculation: stop watching it */
const watches = new Map<string, () => void>();

const valueKey = (v: unknown) => {
  try { return JSON.stringify(v ?? null); } catch { return String(v); }
};

function unmark(ids: Iterable<string>, onlySave?: number) {
  const current = graphStoreJotaiStore.get(pendingAtom);
  const kept = new Map(current);
  let changed = false;
  for (const id of Array.from(ids)) {
    if (!kept.has(id)) continue;
    if (onlySave !== undefined && kept.get(id) !== onlySave) continue;
    kept.delete(id);
    changed = true;
    watches.get(id)?.();
    watches.delete(id);
  }
  if (changed) graphStoreJotaiStore.set(pendingAtom, kept);
}

/** Watch a marked calculation: its mark goes when the server takes over
 *  (it arrives stale), or when a fresh result arrives. */
function watch(exprId: string) {
  watches.get(exprId)?.();
  const expr = graphStore.getNode<any>(exprId) as any;
  const evaluatedAt = expr?.last_evaluated_at ?? null;
  const unsubs: Array<() => void> = [];
  unsubs.push(graphStoreJotaiStore.sub(graphStore.getNodeAtom(exprId), () => {
    const now = graphStore.getNode<any>(exprId) as any;
    if (!now || now.is_stale === true || (now.last_evaluated_at ?? null) !== evaluatedAt) unmark([exprId]);
  }));
  for (const dvId of targetIds(exprId, "result")) {
    const before = valueKey((graphStore.getNode<any>(dvId) as any)?.value);
    unsubs.push(graphStoreJotaiStore.sub(graphStore.getNodeAtom(dvId), () => {
      if (valueKey((graphStore.getNode<any>(dvId) as any)?.value) !== before) unmark([exprId]);
    }));
  }
  watches.set(exprId, () => unsubs.forEach((u) => u()));
}

let saves = 0;

/** Mark what a save will make stale; returns how its marks end. */
export function markSave(event: SaveEvent): ((ok: boolean) => void) | void {
  const ids = affectedExpressions(rootsOfSave(event));
  if (ids.size === 0) return;
  const save = ++saves;
  const marked = new Map(graphStoreJotaiStore.get(pendingAtom));
  ids.forEach((id) => marked.set(id, save));
  graphStoreJotaiStore.set(pendingAtom, marked);
  ids.forEach(watch);
  return (ok: boolean) => {
    if (!ok) { unmark(ids, save); return; }
    setTimeout(() => unmark(ids, save), SETTLE_MS);
  };
}

/** True while a save in flight (or just confirmed) will make this
 *  calculation stale. */
export function usePendingCalculation(exprId: string | null | undefined): boolean {
  return useAtomValue(isPendingAtom(exprId ?? ""), { store: graphStoreJotaiStore });
}

/** Every marked calculation id, for a list that shows many (one
 *  subscription instead of one per row). */
export function usePendingCalculationIds(): ReadonlyMap<string, number> {
  return useAtomValue(pendingAtom, { store: graphStoreJotaiStore });
}

/** The marked calculation ids (tests). */
export function pendingCalculations(): string[] {
  return Array.from(graphStoreJotaiStore.get(pendingAtom).keys());
}

/** Drop every mark (tests; a workspace switch). */
export function clearPendingCalculations() {
  unmark(Array.from(graphStoreJotaiStore.get(pendingAtom).keys()));
}

let installed: (() => void) | null = null;
/** Start marking on every save (idempotent; done on import). */
export function installPendingCalculations() {
  if (!installed) installed = onSave(markSave);
}
installPendingCalculations();
